var isElement = function isElement(el) {
  return el != null && typeof el == 'object' && (el.nodeType === 1 || el.nodeType === 11);
};

var hasScrollableSpace = function hasScrollableSpace(el, axis) {
  return axis === 'X' ? el.clientWidth < el.scrollWidth : el.clientHeight < el.scrollHeight;
};

var canOverflow = function canOverflow(el, axis, skipOverflowHiddenElements) {
  var overflowValue = getComputedStyle(el, null)['overflow' + axis];

  if (skipOverflowHiddenElements && overflowValue === 'hidden') {
    return false;
  }

  return overflowValue !== 'visible' && overflowValue !== 'clip';
};

var isScrollable = function isScrollable(el, skipOverflowHiddenElements) {
  return hasScrollableSpace(el, 'Y') && canOverflow(el, 'Y', skipOverflowHiddenElements) || hasScrollableSpace(el, 'X') && canOverflow(el, 'X', skipOverflowHiddenElements);
};

var alignNearest = function alignNearest(scrollingEdgeStart, scrollingEdgeEnd, scrollingSize, scrollingBorderStart, scrollingBorderEnd, elementEdgeStart, elementEdgeEnd, elementSize) {
  if (elementEdgeStart < scrollingEdgeStart && elementEdgeEnd > scrollingEdgeEnd || elementEdgeStart > scrollingEdgeStart && elementEdgeEnd < scrollingEdgeEnd) {
    return 0;
  }

  if (elementEdgeStart < scrollingEdgeStart && elementSize < scrollingSize || elementEdgeEnd > scrollingEdgeEnd && elementSize > scrollingSize) {
    return elementEdgeStart - scrollingEdgeStart - scrollingBorderStart;
  }

  if (elementEdgeEnd > scrollingEdgeEnd && elementSize < scrollingSize || elementEdgeStart < scrollingEdgeStart && elementSize > scrollingSize) {
    return elementEdgeEnd - scrollingEdgeEnd + scrollingBorderEnd;
  }

  return 0;
};

export default (function (target, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$scrollMode = _options.scrollMode,
      scrollMode = _options$scrollMode === void 0 ? 'always' : _options$scrollMode,
      _options$block = _options.block,
      block = _options$block === void 0 ? 'center' : _options$block,
      _options$inline = _options.inline,
      inline = _options$inline === void 0 ? 'nearest' : _options$inline,
      boundary = _options.boundary,
      _options$skipOverflow = _options.skipOverflowHiddenElements,
      skipOverflowHiddenElements = _options$skipOverflow === void 0 ? false : _options$skipOverflow;
  var checkBoundary = typeof boundary == 'function' ? boundary : function (parent) {
    return parent !== boundary;
  };

  if (!isElement(target)) {
    throw new Error('Element is required in scrollIntoView');
  }

  var targetRect = target.getBoundingClientRect();
  var viewport = document.scrollingElement || document.documentElement;
  var frames = [];
  var parent;

  while (isElement(parent = target.parentNode || target.host) && checkBoundary(target)) {
    if (isScrollable(parent, skipOverflowHiddenElements) || parent === viewport) {
      frames.push(parent);
    }

    target = parent;
  }

  var viewportWidth = window.visualViewport ? window.visualViewport.width : viewport.clientWidth;
  var viewportHeight = window.visualViewport ? window.visualViewport.height : viewport.clientHeight;
  var viewportX = window.scrollX || window.pageXOffset;
  var viewportY = window.scrollY || window.pageYOffset;

  if (scrollMode === 'if-needed') {
    var isVisible = frames.every(function (frame) {
      var frameRect = frame.getBoundingClientRect();

      if (targetRect.top < frameRect.top) {
        return false;
      }

      if (targetRect.bottom > frameRect.bottom) {
        return false;
      }

      if (frame === viewport) {
        if (targetRect.bottom > viewportHeight) {
          return false;
        }

        if (targetRect.left > viewportWidth) {
          return false;
        }
      }

      return true;
    });

    if (isVisible) {
      return [];
    }
  }

  var targetBlock;
  var targetInline;
  var computations = frames.map(function (frame) {
    var frameRect = frame.getBoundingClientRect();
    var frameStyle = getComputedStyle(frame);
    var borderLeft = parseInt(frameStyle.borderLeftWidth, 10);
    var borderTop = parseInt(frameStyle.borderTopWidth, 10);
    var borderRight = parseInt(frameStyle.borderRightWidth, 10);
    var borderBottom = parseInt(frameStyle.borderBottomWidth, 10);
    var blockScroll = 0;
    var inlineScroll = 0;

    if (block === 'start') {
      if (!targetBlock) {
        targetBlock = targetRect.top;
      }

      if (viewport === frame) {
        blockScroll = viewportY + targetBlock;
      } else {
        var offset = Math.min(targetBlock - frameRect.top, frame.scrollHeight - frame.clientHeight - frame.scrollTop);
        blockScroll = frame.scrollTop + offset - borderTop;
      }
    }

    if (block === 'center') {
      if (!targetBlock) {
        targetBlock = targetRect.top + targetRect.height / 2;
      }

      if (viewport === frame) {
        blockScroll = viewportY + targetBlock - frame.clientHeight / 2;
      } else {
        var _offset = 0 - Math.min(frameRect.top + frameRect.height / 2 - targetBlock, frame.scrollTop);

        blockScroll = frame.scrollTop + _offset;
      }
    }

    if (block === 'end') {
      if (!targetBlock) {
        targetBlock = targetRect.bottom;
      }

      if (viewport === frame) {
        blockScroll = viewportY + targetBlock - frame.clientHeight;
      } else {
        var _offset2 = 0 - Math.min(frameRect.bottom - targetBlock, frame.scrollTop);

        blockScroll = frame.scrollTop + _offset2 + borderBottom;
      }
    }

    if (block === 'nearest') {
      if (!targetBlock) {
        targetBlock = targetRect.top;
      }

      if (viewport === frame) {
        var _offset3 = alignNearest(viewportY, viewportY + viewportHeight, viewportHeight, borderTop, borderBottom, viewportY + targetBlock, viewportY + targetBlock + targetRect.height, targetRect.height);

        blockScroll = viewportY + _offset3;
      } else {
        var _offset4 = alignNearest(frameRect.top, frameRect.bottom, frameRect.height, borderTop, borderBottom, targetBlock, targetBlock + targetRect.height, targetRect.height);

        blockScroll = frame.scrollTop + _offset4;
      }
    }

    if (inline === 'start') {
      if (!targetInline) {
        targetInline = targetRect.left;
      }

      if (viewport === frame) {
        inlineScroll = viewportX + targetInline;
      } else {
        var _offset5 = Math.min(targetInline - frameRect.left, frame.scrollHeight - frame.clientLeft - frame.scrollLeft);

        inlineScroll = frame.scrollLeft + _offset5 - borderLeft;
      }
    }

    if (inline === 'center') {
      if (!targetInline) {
        targetInline = targetRect.left + targetRect.width / 2;
      }

      if (viewport === frame) {
        inlineScroll = viewportX + targetInline - frame.clientWidth / 2;
      } else {
        var _offset6 = 0 - Math.min(frameRect.left + frameRect.width / 2 - targetInline, frame.scrollLeft);

        inlineScroll = frame.scrollLeft + _offset6;
      }
    }

    if (inline === 'end') {
      if (!targetInline) {
        targetInline = targetRect.right;
      }

      if (viewport === frame) {
        inlineScroll = viewportX + targetInline - frame.clientWidth;
      } else {
        var _offset7 = 0 - Math.min(frameRect.right - targetInline, frame.scrollLeft);

        inlineScroll = frame.scrollLeft + _offset7 + borderRight;
      }
    }

    if (inline === 'nearest') {
      if (!targetInline) {
        targetInline = targetRect.left;
      }

      if (viewport === frame) {
        var _offset8 = alignNearest(viewportX, viewportX + viewportWidth, viewportWidth, borderLeft, borderRight, viewportX + targetInline, viewportX + targetInline + targetRect.width, targetRect.width);

        inlineScroll = viewportX + _offset8;
      } else {
        var _offset9 = alignNearest(frameRect.left, frameRect.right, frameRect.width, borderLeft, borderRight, targetInline, targetInline + targetRect.width, targetRect.width);

        inlineScroll = frame.scrollLeft + _offset9;
      }
    }

    targetBlock += frame.scrollTop - blockScroll;
    targetInline += frame.scrollLeft - inlineScroll;
    return {
      el: frame,
      top: blockScroll,
      left: inlineScroll
    };
  });
  return computations;
});


//////////////////
// WEBPACK FOOTER
// ./node_modules/scroll-into-view-if-needed/es/compute.js
// module id = 416
// module chunks = 0